/**
 * Device Fingerprinting Library
 * Generates a stable device fingerprint for trusted device authentication
 */

import FingerprintJS from '@fingerprintjs/fingerprintjs';

// Cache the fingerprint agent
let fpPromise: Promise<any> | null = null;

/**
 * Initialize the fingerprinting library
 */
async function getFingerprintAgent() {
  if (!fpPromise) {
    fpPromise = FingerprintJS.load();
  }
  return fpPromise;
}

/**
 * Generate a SHA-256 hash of a string
 */
async function sha256(message: string): Promise<string> {
  const msgUint8 = new TextEncoder().encode(message);
  const hashBuffer = await crypto.subtle.digest('SHA-256', msgUint8);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  return hashHex;
}

/**
 * Get or create a stable device ID stored in localStorage
 */
function getStoredDeviceId(): string {
  const storageKey = 'pcm_device_id';
  
  // Development mode: Allow using a production device ID for testing
  if (import.meta.env.DEV) {
    const devOverride = localStorage.getItem('pcm_dev_device_override');
    if (devOverride) {
      console.log('🔧 Using development device ID override:', devOverride.substring(0, 8) + '...');
      return devOverride;
    }
  }
  
  let deviceId = localStorage.getItem(storageKey);
  
  if (!deviceId) {
    deviceId = crypto.randomUUID();
    localStorage.setItem(storageKey, deviceId);
    
    if (import.meta.env.DEV) {
      console.log('🆔 Generated new device ID for local development:', deviceId.substring(0, 8) + '...');
      console.log('💡 To sync with production device, use: setDeviceIdOverride("your-production-device-id")');
    }
  }
  
  return deviceId;
}

/**
 * Generate device metadata for additional context
 */
function getDeviceMetadata(): Record<string, any> {
  const metadata: Record<string, any> = {};
  
  // Browser information
  const userAgent = navigator.userAgent;
  metadata.userAgent = userAgent;
  
  // Platform
  metadata.platform = navigator.platform || 'unknown';
  
  // Screen resolution
  metadata.screenResolution = `${window.screen.width}x${window.screen.height}`;
  metadata.colorDepth = window.screen.colorDepth;
  
  // Timezone
  metadata.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  
  // Language
  metadata.language = navigator.language;
  
  // Browser features
  metadata.cookieEnabled = navigator.cookieEnabled;
  metadata.doNotTrack = navigator.doNotTrack;
  
  // Hardware concurrency (CPU cores)
  metadata.hardwareConcurrency = navigator.hardwareConcurrency;
  
  // Device memory (if available)
  if ('deviceMemory' in navigator) {
    metadata.deviceMemory = (navigator as any).deviceMemory;
  }
  
  // Connection type (if available)
  if ('connection' in navigator && (navigator as any).connection) {
    const conn = (navigator as any).connection;
    metadata.connectionType = conn.effectiveType;
  }
  
  // Additional device-specific features
  metadata.pixelRatio = window.devicePixelRatio;
  metadata.touchSupport = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  metadata.maxTouchPoints = navigator.maxTouchPoints || 0;
  
  // WebGL vendor/renderer (helps identify GPU - more device specific)
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    if (gl) {
      const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
      if (debugInfo) {
        metadata.webglVendor = gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL);
        metadata.webglRenderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);
      }
    }
  } catch (e) {
    // WebGL not available
  }
  
  // Media devices (camera/microphone count)
  if (navigator.mediaDevices && navigator.mediaDevices.enumerateDevices) {
    navigator.mediaDevices.enumerateDevices().then(devices => {
      metadata.audioInputCount = devices.filter(d => d.kind === 'audioinput').length;
      metadata.videoInputCount = devices.filter(d => d.kind === 'videoinput').length;
    }).catch(() => {});
  }
  
  return metadata;
}

/**
 * Parse user agent to extract browser and OS information
 */
function parseUserAgent(userAgent: string): { browser?: string; os?: string; deviceType?: string } {
  const result: { browser?: string; os?: string; deviceType?: string } = {};
  
  // Browser detection
  if (userAgent.includes('Firefox/')) {
    result.browser = 'Firefox';
  } else if (userAgent.includes('Chrome/') && !userAgent.includes('Edg/')) {
    result.browser = 'Chrome';
  } else if (userAgent.includes('Safari/') && !userAgent.includes('Chrome')) {
    result.browser = 'Safari';
  } else if (userAgent.includes('Edg/')) {
    result.browser = 'Edge';
  }
  
  // OS and device type detection
  if (userAgent.includes('iPhone')) {
    result.os = 'iOS';
    result.deviceType = 'iPhone';
  } else if (userAgent.includes('iPad')) {
    result.os = 'iOS';
    result.deviceType = 'iPad';
  } else if (userAgent.includes('Android')) {
    result.os = 'Android';
    result.deviceType = userAgent.includes('Mobile') ? 'Phone' : 'Tablet';
  } else if (userAgent.includes('Windows')) {
    result.os = 'Windows';
    result.deviceType = 'PC';
  } else if (userAgent.includes('Mac OS X')) {
    result.os = 'macOS';
    // Try to detect Mac model
    if (userAgent.includes('MacBook')) {
      result.deviceType = 'MacBook';
    } else if (userAgent.includes('iMac')) {
      result.deviceType = 'iMac';
    } else {
      result.deviceType = 'Mac';
    }
  } else if (userAgent.includes('Linux')) {
    result.os = 'Linux';
    result.deviceType = 'PC';
  }
  
  return result;
}

/**
 * Generate a stable device hash for authentication
 */
export async function generateDeviceHash(): Promise<string> {
  try {
    // Get FingerprintJS visitor ID
    const fp = await getFingerprintAgent();
    const result = await fp.get();
    const visitorId = result.visitorId;
    
    // Get stored device ID for additional stability
    const storedId = getStoredDeviceId();
    
    // Get additional device-specific metadata
    const metadata = getDeviceMetadata();
    
    // Combine multiple factors for the hash
    const factors = [
      visitorId,
      storedId,
      navigator.userAgent,
      navigator.language,
      window.screen.width,
      window.screen.height,
      new Date().getTimezoneOffset(),
      // Additional device-specific factors
      navigator.hardwareConcurrency, // CPU cores
      metadata.pixelRatio,
      metadata.webglRenderer, // GPU info
      metadata.touchSupport ? 'touch' : 'no-touch',
      metadata.deviceMemory || 'unknown'
    ];
    
    // Create composite string
    const composite = factors.filter(Boolean).join('|');
    
    // Generate SHA-256 hash
    const hash = await sha256(composite);
    
    if (import.meta.env.DEV) {
      console.log('🔐 Device fingerprint generated:', {
        hash: hash.substring(0, 8) + '...',
        visitorId: visitorId.substring(0, 8) + '...',
        storedId: storedId.substring(0, 8) + '...',
        factorsUsed: factors.length,
        composite: composite.substring(0, 50) + '...'
      });
    }
    
    return hash;
  } catch (error) {
    console.error('Error generating device hash:', error);
    
    // Fallback to basic fingerprinting if FingerprintJS fails
    const fallbackFactors = [
      getStoredDeviceId(),
      navigator.userAgent,
      navigator.platform,
      navigator.language,
      window.screen.width,
      window.screen.height
    ];
    
    const fallbackComposite = fallbackFactors.filter(Boolean).join('|');
    return sha256(fallbackComposite);
  }
}

/**
 * Get device information for registration
 */
export async function getDeviceInfo(): Promise<{
  hash: string;
  name: string;
  metadata: Record<string, any>;
}> {
  const hash = await generateDeviceHash();
  const metadata = getDeviceMetadata();
  const { browser, os, deviceType } = parseUserAgent(navigator.userAgent);
  
  // Generate a more specific device name
  const nameParts = [];
  
  // Include browser
  if (browser) nameParts.push(browser);
  
  // Include device type and OS
  if (deviceType && os) {
    nameParts.push(`on ${deviceType}`);
    // Add OS version if different from device type
    if (deviceType !== os && !deviceType.includes(os)) {
      nameParts.push(`(${os})`);
    }
  } else if (os) {
    nameParts.push(`on ${os}`);
  }
  
  // Add distinguishing features for similar devices
  if (metadata.touchSupport && !deviceType?.includes('Phone') && !deviceType?.includes('Tablet') && !deviceType?.includes('iPad')) {
    nameParts.push('Touch');
  }
  
  if (nameParts.length === 0) {
    nameParts.push('Unknown Device');
  }
  
  return {
    hash,
    name: nameParts.join(' '),
    metadata: {
      ...metadata,
      browser,
      os,
      deviceType,
      platform: navigator.platform
    }
  };
}

/**
 * Clear stored device ID (useful for testing or logout)
 */
export function clearDeviceId(): void {
  localStorage.removeItem('pcm_device_id');
}

/**
 * Check if device fingerprinting is supported
 */
export function isDeviceFingerprintingSupported(): boolean {
  // Check for required APIs
  const hasSubtleCrypto = 'crypto' in window && 'subtle' in window.crypto;
  const hasLocalStorage = 'localStorage' in window;
  
  try {
    // Test localStorage access
    const testKey = '__test__';
    localStorage.setItem(testKey, '1');
    localStorage.removeItem(testKey);
    
    return hasSubtleCrypto && hasLocalStorage;
  } catch {
    return false;
  }
}

/**
 * Device trust status
 */
export interface DeviceTrustStatus {
  isTrusted: boolean;
  deviceId?: string;
  expiresAt?: Date;
  lastSeen?: Date;
}

/**
 * Check device trust status with the backend
 */
export async function checkDeviceTrust(supabase: any): Promise<DeviceTrustStatus> {
  try {
    const deviceHash = await generateDeviceHash();
    
    const { data, error } = await supabase.functions.invoke('validate-device', {
      headers: {
        'x-device-hash': deviceHash
      }
    });
    
    if (error || !data) {
      return { isTrusted: false };
    }
    
    return {
      isTrusted: data.isValid && data.trustStatus === 'trusted',
      deviceId: data.deviceId,
      expiresAt: data.expiresAt ? new Date(data.expiresAt) : undefined,
      lastSeen: data.lastSeen ? new Date(data.lastSeen) : undefined
    };
  } catch (error) {
    console.error('Error checking device trust:', error);
    return { isTrusted: false };
  }
}

/**
 * Register current device as trusted
 */
export async function registerTrustedDevice(
  supabase: any,
  deviceName?: string,
  trustDuration?: number
): Promise<{ success: boolean; message?: string }> {
  try {
    const deviceInfo = await getDeviceInfo();
    
    const { data, error } = await supabase.functions.invoke('register-device', {
      body: {
        deviceHash: deviceInfo.hash,
        deviceName: deviceName || deviceInfo.name,
        metadata: deviceInfo.metadata,
        trustDuration
      }
    });
    
    if (error) {
      return { 
        success: false, 
        message: error.message || 'Failed to register device' 
      };
    }
    
    return {
      success: data.success,
      message: data.message
    };
  } catch (error) {
    console.error('Error registering device:', error);
    return { 
      success: false, 
      message: 'An error occurred while registering the device' 
    };
  }
}

/**
 * Development helper: Set a device ID override for testing
 * This allows local development to use the same device ID as production
 */
export function setDeviceIdOverride(deviceId: string): void {
  if (!import.meta.env.DEV) {
    console.warn('Device ID override is only available in development mode');
    return;
  }
  
  localStorage.setItem('pcm_dev_device_override', deviceId);
  console.log('✅ Device ID override set:', deviceId.substring(0, 8) + '...');
  console.log('🔄 Refresh the page to use the new device ID');
}

/**
 * Development helper: Clear device ID override
 */
export function clearDeviceIdOverride(): void {
  if (!import.meta.env.DEV) {
    console.warn('Device ID override is only available in development mode');
    return;
  }
  
  localStorage.removeItem('pcm_dev_device_override');
  console.log('✅ Device ID override cleared - will use local device ID');
  console.log('🔄 Refresh the page to use the local device ID');
}

/**
 * Development helper: Show current device information
 */
export async function showDeviceDebugInfo(): Promise<void> {
  if (!import.meta.env.DEV) {
    console.warn('Device debug info is only available in development mode');
    return;
  }
  
  const deviceInfo = await getDeviceInfo();
  const storedId = getStoredDeviceId();
  const override = localStorage.getItem('pcm_dev_device_override');
  
  console.group('🔍 Device Debug Information');
  console.log('Device Hash:', deviceInfo.hash);
  console.log('Device Name:', deviceInfo.name);
  console.log('Stored Device ID:', storedId);
  console.log('Has Override:', !!override);
  if (override) {
    console.log('Override Value:', override.substring(0, 8) + '...');
  }
  console.log('Metadata:', deviceInfo.metadata);
  console.groupEnd();
}

// Make debug functions available on window in development
if (import.meta.env.DEV && typeof window !== 'undefined') {
  (window as any).setDeviceIdOverride = setDeviceIdOverride;
  (window as any).clearDeviceIdOverride = clearDeviceIdOverride;
  (window as any).showDeviceDebugInfo = showDeviceDebugInfo;
}